<script lang="ts">
/**
 * Dashboard section highlighting items that need cataloging work.
 *
 * Two responsive modes: on mobile, shows compact count chips
 * ("3 missing photos"). On desktop, shows full item lists grouped
 * by what's missing (photos, descriptions).
 *
 * Renders nothing when both lists are empty.
 *
 * @example
 * ```vue
 * <NeedsAttentionSection
 *   :missing-photos="items_without_photos"
 *   :missing-descriptions="items_without_description"
 * />
 * ```
 */
export interface NeedsAttentionSectionProps {
  /** Items that have no photos. */
  missingPhotos: ItemSummary[]
  /** Items that have no description. */
  missingDescriptions: ItemSummary[]
}
</script>

<script setup lang="ts">
import { SbSectionHeader, SbList, SbListLabel } from "@unio/saltbox-ui"

import ListItemRow from "@/components/ListItemRow.vue"
import { ICON } from "@/lib/icons"
import type { ItemSummary } from "@/types/models"

defineProps<NeedsAttentionSectionProps>()
</script>

<template>
  <div v-if="missingPhotos.length || missingDescriptions.length">
    <!-- Mobile: compact counts only -->
    <div class="bg-elevated text-muted flex gap-4 rounded-lg p-4 text-xs lg:hidden">
      <div v-if="missingPhotos.length" class="flex items-center gap-2">
        <UIcon :name="ICON.capture" class="size-4" />
        {{ missingPhotos.length }} missing photos
      </div>
      <div v-if="missingDescriptions.length" class="flex items-center gap-2">
        <UIcon :name="ICON.edit" class="size-4" />
        {{ missingDescriptions.length }} missing description
      </div>
    </div>

    <!-- Desktop: full lists -->
    <div class="hidden lg:block">
      <SbSectionHeader label="Needs Attention" />
      <div class="mt-2 grid grid-cols-2 gap-6">
        <div v-if="missingPhotos.length">
          <SbList separator>
            <SbListLabel header>Missing photos</SbListLabel>
            <ListItemRow
              v-for="item in missingPhotos"
              :key="item.id"
              :item="item"
              variant="compact"
            />
          </SbList>
        </div>
        <div v-if="missingDescriptions.length">
          <SbList separator>
            <SbListLabel header>Missing description</SbListLabel>
            <ListItemRow
              v-for="item in missingDescriptions"
              :key="item.id"
              :item="item"
              variant="compact"
            />
          </SbList>
        </div>
      </div>
    </div>
  </div>
</template>
